import moment from "moment";

// Company time (final-version doc 2026-10-05, problem 4 "one hour difference").
// Every timestamp is shown in Damascus wall-clock time, which is a FIXED
// UTC+3 (Syria has had no DST since October 2022). The viewer's PC time zone
// is never used: a PC on +02 or +04 with a hand-corrected clock used to show
// ISO "Z" values one hour off while the finance pages (already +180) were
// right. moment.now is server-synced (util/index.js setMomentTime), so
// companyNow() is the server's instant shown on the company clock.
export var COMPANY_UTC_OFFSET_MINUTES = 180;

// A value that carries its own zone: "...T11:00:00.000000Z", "...+00:00".
var ZONED = /\d{2}:\d{2}(:\d{2}(\.\d+)?)?\s*([zZ]|[+-]\d{2}(:?\d{2})?)$/;
var DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
// Naive server strings are already Damascus wall time (Accounting, Booking,
// Notification and the finance models serialize "Y-m-d H:i:s").
var NAIVE_FORMATS = [
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD HH:mm:ss.SSSSSS",
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
  "YYYY-MM-DDTHH:mm:ss.SSSSSS",
  "YYYY-MM-DDTHH:mm:ss.SSS",
];

function wallClock(text, formats) {
  // Build the fields in UTC, then pin them to +180 keeping the wall clock,
  // so the browser zone never takes part.
  var parsed = moment.utc(text, formats, true);
  return parsed.isValid()
    ? parsed.utcOffset(COMPANY_UTC_OFFSET_MINUTES, true)
    : null;
}

// Returns a moment fixed at +180 holding the Damascus wall clock, or null.
export function toCompanyMoment(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (moment.isMoment(value) || value instanceof Date) {
    var instant = moment(value);
    return instant.isValid()
      ? instant.utcOffset(COMPANY_UTC_OFFSET_MINUTES)
      : null;
  }

  if (typeof value === "number") {
    // Epoch milliseconds.
    return isFinite(value)
      ? moment(value).utcOffset(COMPANY_UTC_OFFSET_MINUTES)
      : null;
  }

  var text = String(value).trim();
  if (text === "") {
    return null;
  }

  if (DATE_ONLY.test(text)) {
    // A calendar date has no zone: it passes through unshifted.
    return wallClock(text, "YYYY-MM-DD");
  }

  if (ZONED.test(text)) {
    var zoned = moment.parseZone(text, moment.ISO_8601);
    if (zoned.isValid()) {
      return zoned.utcOffset(COMPANY_UTC_OFFSET_MINUTES);
    }
  }

  return wallClock(text, NAIVE_FORMATS);
}

export function formatCompanyTime(value, format, fallback) {
  if (fallback === undefined) {
    fallback = "-";
  }
  var m = toCompanyMoment(value);
  return m ? m.format(format || "YYYY-MM-DD HH:mm") : fallback;
}

// Relative age ("5 minutes ago") measured against the server-synced clock.
export function companyFromNow(value, fallback) {
  var m = toCompanyMoment(value);
  if (!m) {
    return fallback === undefined ? "-" : fallback;
  }
  return m.from(companyNow());
}

export function companyNow() {
  return moment().utcOffset(COMPANY_UTC_OFFSET_MINUTES);
}

export function companyToday(format) {
  return companyNow().format(format || "YYYY-MM-DD");
}

export default {
  COMPANY_UTC_OFFSET_MINUTES: COMPANY_UTC_OFFSET_MINUTES,
  toCompanyMoment: toCompanyMoment,
  formatCompanyTime: formatCompanyTime,
  companyFromNow: companyFromNow,
  companyNow: companyNow,
  companyToday: companyToday,
};
